// ============================================================================
// High-frequency telemetry write buffer.
//
// Streaming 1Hz frames straight into Postgres via single-row REST inserts
// exhausts the connection pool and bloats storage. Instead we accumulate raw
// frames in memory, fold them into 10-second rolling-average windows, and flush
// compact batches only at interval boundaries, rest periods, or teardown.
//
// On a recovered BLE drop (see ConnectionStateMachine) a short gap can be
// linearly backfilled so downstream curves stay continuous.
// ============================================================================

import type { TelemetryFrame, TelemetryMetric } from './types'

export type TelemetryWindow = {
  windowStart: number
  windowEnd: number
  sampleCount: number
  /** 10s mean per metric (or last value for counters like distance). */
  averages: Partial<Record<TelemetryMetric, number>>
  /** Peak per metric within the window (drives peak power/velocity). */
  peaks: Partial<Record<TelemetryMetric, number>>
}

const WINDOW_MS = 10_000
const MEAN_METRICS: TelemetryMetric[] = [
  'speed_kmh', 'incline_pct', 'cadence_rpm', 'resistance_level', 'stride_rate',
  'power_watts', 'stroke_rate', 'split_500m_sec', 'mean_velocity_ms', 'heart_rate_bpm',
]
const COUNTER_METRICS: TelemetryMetric[] = ['distance_m', 'calories_kcal', 'elapsed_s', 'rep_count']
const PEAK_METRICS: TelemetryMetric[] = ['power_watts', 'peak_velocity_ms', 'mean_velocity_ms', 'heart_rate_bpm']

type FlushHandler = (windows: TelemetryWindow[]) => Promise<void> | void

export class TelemetryBuffer {
  private raw: TelemetryFrame[] = []
  private sealedWindows: TelemetryWindow[] = []
  private currentWindowStart = 0
  private sessionStarted = false
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private readonly flushHandler: FlushHandler
  private readonly flushIntervalMs: number

  /** `flushIntervalMs` is the wall-clock cadence to push sealed windows. */
  constructor(flushHandler: FlushHandler, flushIntervalMs = 30_000) {
    this.flushHandler = flushHandler
    this.flushIntervalMs = flushIntervalMs
  }

  start(): void {
    if (this.flushTimer) return
    this.flushTimer = setInterval(() => void this.flush(), this.flushIntervalMs)
  }

  /** Append a live frame; seals the prior 10s window when the boundary passes. */
  ingest(frame: TelemetryFrame): void {
    if (!this.sessionStarted) {
      this.currentWindowStart = frame.timestamp
      this.sessionStarted = true
    }
    if (frame.timestamp - this.currentWindowStart >= WINDOW_MS) {
      this.sealCurrentWindow()
      this.currentWindowStart = frame.timestamp
    }
    this.raw.push(frame)
  }

  private sealCurrentWindow(): void {
    const windowFrames = this.raw.filter(
      (f) => f.timestamp >= this.currentWindowStart && f.timestamp < this.currentWindowStart + WINDOW_MS,
    )
    if (windowFrames.length === 0) return

    const sums: Partial<Record<TelemetryMetric, number>> = {}
    const counts: Partial<Record<TelemetryMetric, number>> = {}
    const lasts: Partial<Record<TelemetryMetric, number>> = {}
    const peaks: Partial<Record<TelemetryMetric, number>> = {}

    for (const frame of windowFrames) {
      for (const [key, value] of Object.entries(frame.metrics) as [TelemetryMetric, number][]) {
        if (value == null) continue
        if (MEAN_METRICS.includes(key)) {
          sums[key] = (sums[key] ?? 0) + value
          counts[key] = (counts[key] ?? 0) + 1
        }
        if (COUNTER_METRICS.includes(key)) lasts[key] = value
        if (PEAK_METRICS.includes(key)) peaks[key] = Math.max(peaks[key] ?? 0, value)
      }
    }

    const averages: Partial<Record<TelemetryMetric, number>> = { ...lasts }
    for (const key of Object.keys(sums) as TelemetryMetric[]) {
      averages[key] = Math.round((sums[key]! / counts[key]!) * 100) / 100
    }

    this.sealedWindows.push({
      windowStart: this.currentWindowStart,
      windowEnd: this.currentWindowStart + WINDOW_MS,
      sampleCount: windowFrames.length,
      averages,
      peaks,
    })
    // Drop raw frames we've folded; keep memory flat regardless of session length.
    this.raw = this.raw.filter((f) => f.timestamp >= this.currentWindowStart + WINDOW_MS)
  }

  /**
   * Backfill a recovered connection gap by linearly interpolating sealed-window
   * boundary values. Only invoked for short, backfillable gaps.
   */
  backfillGap(droppedAt: number, recoveredAt: number): void {
    const before = this.sealedWindows.filter((w) => w.windowEnd <= droppedAt).at(-1)
    const after = this.sealedWindows.find((w) => w.windowStart >= recoveredAt)
    if (!before || !after) return

    const steps = Math.max(1, Math.round((recoveredAt - droppedAt) / WINDOW_MS))
    for (let i = 1; i < steps; i++) {
      const t = before.windowEnd + i * WINDOW_MS
      const ratio = i / steps
      const averages: Partial<Record<TelemetryMetric, number>> = {}
      for (const key of MEAN_METRICS) {
        const a = before.averages[key]
        const b = after.averages[key]
        if (a != null && b != null) averages[key] = Math.round((a + (b - a) * ratio) * 100) / 100
      }
      this.sealedWindows.push({
        windowStart: t,
        windowEnd: t + WINDOW_MS,
        sampleCount: 0,
        averages,
        peaks: {},
      })
    }
    this.sealedWindows.sort((a, b) => a.windowStart - b.windowStart)
  }

  /** Push all sealed windows to the backend and clear them. */
  async flush(): Promise<void> {
    if (this.sealedWindows.length === 0) return
    const batch = this.sealedWindows
    this.sealedWindows = []
    try {
      await this.flushHandler(batch)
    } catch (err) {
      // Re-queue on failure so a transient network blip doesn't lose data.
      this.sealedWindows = [...batch, ...this.sealedWindows]
      console.warn('TelemetryBuffer flush failed, re-queued:', err)
    }
  }

  /** Final teardown: seal the trailing partial window and flush everything. */
  async finalizeAndFlush(): Promise<void> {
    this.sealCurrentWindow()
    if (this.flushTimer) clearInterval(this.flushTimer)
    this.flushTimer = null
    await this.flush()
  }

  pendingWindowCount(): number {
    return this.sealedWindows.length
  }
}
